import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CircleDollarSign, ChevronDown, ChevronUp, Info, Wrench } from "lucide-react";

const formatCOP = (n) => `$ ${Math.round(n || 0).toLocaleString("es-CO")}`;
const num = (n) => Number(n || 0).toLocaleString("es-CO", { maximumFractionDigits: 2 });

/** Finanzas → Lotes y despachos a costo $0 (ventas que salen con 100% de margen). Permite corregir. */
export default function CostoCeroPanel({ onCorregido }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState({});
  const [costos, setCostos] = useState({});
  const [aplicando, setAplicando] = useState(false);

  const cargar = () => {
    setLoading(true);
    api.get("/reportes/auditoria-costo-cero")
      .then(({ data }) => {
        setData(data);
        const s = {}, c = {};
        data.productos.forEach((r) => { s[r.producto_id] = r.costo_ficha > 0; c[r.producto_id] = r.costo_ficha || ""; });
        setSel(s); setCostos(c);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo revisar los costos"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open && !data) cargar(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const seleccionados = (data?.productos || []).filter((r) => sel[r.producto_id]);

  const corregir = async () => {
    const sinCosto = seleccionados.filter((r) => !(Number(costos[r.producto_id]) > 0));
    if (sinCosto.length) { toast.error(`Escribe un costo mayor a $0 para: ${sinCosto.map((r) => r.codigo || r.producto).slice(0, 3).join(", ")}`); return; }
    if (!window.confirm(`Se corregirán ${seleccionados.length} producto(s): lotes a $0, despachos históricos y costo de los pedidos vendidos. La utilidad en Finanzas bajará en consecuencia. ¿Continuar?`)) return;
    setAplicando(true);
    try {
      const { data: r } = await api.post("/reportes/auditoria-costo-cero/corregir", {
        productos: seleccionados.map((p) => ({ producto_id: p.producto_id, costo: Number(costos[p.producto_id]) })),
        motivo: "Corrección costo $0 desde Finanzas",
      });
      toast.success(`Corregido: ${r.lotes} lote(s), ${r.movimientos} despacho(s), ${r.items} ítem(s) en ${r.pedidos} pedido(s). COGS agregado ${formatCOP(r.cogs_agregado)}.`, { duration: 8000 });
      setData(null); cargar(); onCorregido?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo corregir");
    } finally { setAplicando(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 mb-6" data-testid="costo-cero-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 text-left" data-testid="costo-cero-toggle">
        <div className="flex items-center gap-2">
          <CircleDollarSign className="w-4 h-4 text-amber-600" />
          <div>
            <div className="text-sm font-semibold text-foreground">Lotes y despachos a costo $0 · ¿hay ventas que salen con 100% de margen?</div>
            <div className="text-xs text-muted-foreground">Detecta lotes iniciales creados sin costo, despachos consumidos a $0 y pedidos con costo $0. Permite corregirlos con el costo de la ficha.</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          {loading && <div className="text-sm text-muted-foreground">Revisando lotes y despachos…</div>}
          {data && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <Kpi label="Productos afectados" value={data.productos_afectados} sub={`${data.lotes_cero} lote(s) a $0`} warn={data.productos_afectados > 0} testid="costo-cero-productos" />
                <Kpi label="Despachos a $0" value={data.despachos_cero} sub={`${num(data.unidades_cero)} unidades vendidas sin costo`} warn={data.despachos_cero > 0} testid="costo-cero-despachos" />
                <Kpi label="Costo que falta en Finanzas" value={formatCOP(data.cogs_faltante_total)} sub="Utilidad sobreestimada (a costo de ficha)" warn={data.cogs_faltante_total > 0} testid="costo-cero-cogs" />
                <Kpi label="Sin costo en ficha" value={data.sin_costo_ficha} sub="Debes escribir el costo a mano" warn={data.sin_costo_ficha > 0} testid="costo-cero-sin-ficha" />
              </div>
              <div className="flex items-start gap-2 text-[11px] text-muted-foreground bg-muted/40 rounded-md p-2">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <div>El lote <b>INICIAL</b> se crea con el costo que tenía la ficha en ese momento; si el producto se cargó sin costo, el lote quedó en $0 y todo lo despachado desde él también. Al corregir se usa el costo que escribas (por defecto el de la ficha) para el lote, los despachos históricos y los pedidos vendidos.</div>
              </div>
              {data.productos_afectados === 0 && <div className="text-sm text-emerald-700" data-testid="costo-cero-ok">Ningún lote ni despacho a costo $0.</div>}
              {data.productos_afectados > 0 && (
                <>
                  <div className="max-h-96 overflow-auto rounded-md border border-border">
                    <table className="w-full text-[11px]">
                      <thead className="bg-muted/50 sticky top-0"><tr>
                        <th className="p-2"><input type="checkbox" checked={seleccionados.length === data.productos.length} onChange={(e) => { const s = {}; data.productos.forEach((r) => { s[r.producto_id] = e.target.checked; }); setSel(s); }} data-testid="costo-cero-sel-todos" /></th>
                        <th className="text-left p-2">Producto</th><th className="text-right p-2">Lotes $0</th><th className="text-right p-2">Stock en lotes $0</th><th className="text-right p-2">Despachos $0</th><th className="text-right p-2">Unid. vendidas $0</th><th className="text-right p-2">Costo ficha</th><th className="text-right p-2">Costo a aplicar</th><th className="text-right p-2">Costo faltante</th>
                      </tr></thead>
                      <tbody>
                        {data.productos.map((r, i) => (
                          <tr key={r.producto_id} className="border-t border-border/60 tabular-nums" data-testid={`costo-cero-fila-${i}`}>
                            <td className="p-2 text-center"><input type="checkbox" checked={!!sel[r.producto_id]} onChange={(e) => setSel((s) => ({ ...s, [r.producto_id]: e.target.checked }))} data-testid={`costo-cero-sel-${i}`} /></td>
                            <td className="p-2 max-w-[220px]"><div className="truncate font-medium">{r.producto}{r.archivado ? " (archivado)" : ""}</div>{r.codigo && <div className="text-muted-foreground">{r.codigo}</div>}{r.lotes.length > 0 && <div className="text-[10px] text-muted-foreground">{r.lotes.map((l) => l.contenedor_id || "—").join(", ")}</div>}</td>
                            <td className="p-2 text-right">{r.lotes.length}</td>
                            <td className="p-2 text-right">{num(r.stock_en_lotes_cero)}</td>
                            <td className="p-2 text-right">{r.despachos}{r.items_snapshot ? ` +${r.items_snapshot} sin FIFO` : ""}</td>
                            <td className="p-2 text-right">{num(r.unidades_total)}</td>
                            <td className={`p-2 text-right ${r.costo_ficha > 0 ? "" : "text-rose-600 font-semibold"}`}>{r.costo_ficha > 0 ? formatCOP(r.costo_ficha) : "sin costo"}</td>
                            <td className="p-2 text-right"><input type="number" min="1" value={costos[r.producto_id] ?? ""} onChange={(e) => setCostos((c) => ({ ...c, [r.producto_id]: e.target.value }))} className="h-7 w-24 rounded border border-border bg-background px-1 text-right" data-testid={`costo-cero-costo-${i}`} /></td>
                            <td className={`p-2 text-right ${r.cogs_faltante > 0 ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>{r.cogs_faltante ? formatCOP(r.cogs_faltante) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-end">
                    <button type="button" disabled={aplicando || seleccionados.length === 0} onClick={corregir} className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 disabled:opacity-50" data-testid="costo-cero-corregir-btn">
                      <Wrench className="w-3.5 h-3.5" /> {aplicando ? "Corrigiendo…" : `Corregir ${seleccionados.length} producto(s) con el costo indicado`}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, warn, testid }) {
  return (
    <div className={`rounded-lg border p-2.5 ${warn ? "border-amber-300 bg-amber-50/60 dark:bg-amber-900/15" : "border-border bg-muted/20"}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-base font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground truncate" title={sub}>{sub}</div>}
    </div>
  );
}
